import { tenantApi, type Group, type GroupMemberUser } from './tenantApi';

export interface WizardPrerequisites {
  groups: Group[];
  canManageSchedules: boolean;
  members: Record<string, GroupMemberUser[]>;
  leaders: Record<string, string[]>;
}

export async function loadWizardPrerequisites(groupIds: string[] = []): Promise<WizardPrerequisites> {
  const [groups, current] = await Promise.all([tenantApi.getGroups(), tenantApi.getCurrentUser()]);
  const user: unknown = current;
  if (!user || typeof user !== 'object') {
    throw new Error('Schedule permissions could not be verified. Refresh prerequisites or contact your tenant administrator.');
  }
  const roles: string[] = 'roles' in user && Array.isArray(user.roles) &&
    user.roles.every((value: unknown) => typeof value === 'string') ? user.roles : [];
  const tenantAdmin = roles.some(role => ['tenantadmin', 'tenant admin'].includes(role.toLowerCase()));
  const permissions: string[] | null = 'permissions' in user && Array.isArray(user.permissions) &&
    user.permissions.every((value: unknown) => typeof value === 'string') ? user.permissions : null;
  if (!tenantAdmin && !permissions) {
    throw new Error('Schedule permissions could not be verified. Refresh prerequisites or contact your tenant administrator.');
  }
  const resolved = await Promise.all([...new Set(groupIds.filter(Boolean))].map(async groupId => {
    const group = groups.find(item => item.groupId === groupId);
    if (!group) throw new Error(`Selected group ${groupId} is no longer available in this tenant. Choose an available resource group or ask your administrator to restore its access, then refresh prerequisites.`);
    const readGroup = async <T,>(description: string, read: () => Promise<T>) => {
      try { return await read(); }
      catch (failure) {
        throw new Error(`Could not load ${description} for group "${group.name}". ${failure instanceof Error ? failure.message : 'Group request failed'}. Check your group access or ask your administrator to check the group API, then Refresh prerequisites. Your roster choices are retained.`);
      }
    };
    const [members, leads] = await Promise.all([
      readGroup('eligible members', () => tenantApi.getGroupMembers(groupId)),
      readGroup('team leaders', () => tenantApi.getGroupTeamLeads(groupId)),
    ]);
    return { groupId, members, leaders: leads.teamLeadUserIds };
  }));
  return {
    groups,
    canManageSchedules: tenantAdmin || !!permissions?.some(permission => permission.toLowerCase() === 'manageschedules'),
    members: Object.fromEntries(resolved.map(group => [group.groupId, group.members])),
    leaders: Object.fromEntries(resolved.map(group => [group.groupId, group.leaders])),
  };
}

export function activeWizardMembers(snapshot: WizardPrerequisites, groupId: string): GroupMemberUser[] {
  return (snapshot.members[groupId] ?? []).filter(member => member.status.toLowerCase() === 'active');
}
